// Create a new channel post: title, Russian description, price, brand,
// category, source URL, and image attachments. Admins can publish
// immediately; managers create a pending post.
//
// Quick-paste workflow for WhatsApp-forwarded products:
//   1. Vendor sends image + price in WhatsApp.
//   2. Operator clicks the WhatsApp image, copies it (right-click → copy
//      image; or on Mac WhatsApp Desktop, ⌘C).
//   3. Opens this modal, pastes with ⌘V anywhere in the form → image
//      lands in the upload preview without a file-picker round-trip.
//   4. Drag-and-drop from another app window also works.
//   5. Types title + price (paste from WhatsApp text). Publish.
import { useState, useRef, useEffect, useCallback } from "react";
import { api } from "../api.js";
import { T, T_NEW } from "../theme.js";
import { I } from "../icons.jsx";
import { Btn, Inp, Modal, Mono } from "./ui.jsx";
import { useT } from "../i18n.jsx";

// Image upload sub-widget. Picks files via file-picker, clipboard paste,
// or drag-and-drop; uploads each via api.uploadImage and shows previews.
// Exposes the array of uploaded paths via onChange.
//
// Paste/drop are scoped to the modal root via the rootRef prop so they
// don't fire when the user is interacting with something else on the
// page (e.g. pasting a Wise transfer ID into a DM chat).
function ImageUploader({ paths, onChange, rootRef }) {
  const t = useT();
  const [uploading, setUploading] = useState(0);
  const [err, setErr] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);
  const cameraRef = useRef(null);
  // Hold the latest paths in a ref so the paste handler (which is set up
  // once on mount) always sees the current array when appending.
  const pathsRef = useRef(paths);
  useEffect(() => { pathsRef.current = paths; }, [paths]);
  // Coarse pointer = touch device (phones, most tablets). We use this to
  // swap the hint copy and surface a "take photo" button that opens the
  // OS camera directly — desktop browsers ignore `capture` so we only
  // bother rendering it on touch.
  const isTouch = typeof window !== "undefined"
    && window.matchMedia
    && window.matchMedia("(pointer: coarse)").matches;

  const onFiles = useCallback(async (files) => {
    setErr("");
    const imageFiles = Array.from(files).filter((f) => f && f.type && f.type.startsWith("image/"));
    if (imageFiles.length === 0) return;
    setUploading((u) => u + imageFiles.length);
    for (const file of imageFiles) {
      try {
        const r = await api.uploadImage(file);
        onChange([...pathsRef.current, { path: r.path, url: r.url, width: r.width, height: r.height }]);
      } catch (e) {
        setErr(e.message || "Upload failed");
      } finally {
        setUploading((u) => u - 1);
      }
    }
  }, [onChange]);

  // Wire ⌘V on the modal root. We listen for `paste` rather than
  // keydown so the OS does the actual clipboard read; we just sniff the
  // files out of event.clipboardData.items.
  useEffect(() => {
    const root = rootRef?.current;
    if (!root) return;
    const handler = (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      const files = [];
      for (const it of items) {
        if (it.kind === "file" && it.type.startsWith("image/")) {
          const f = it.getAsFile();
          if (f) files.push(f);
        }
      }
      if (files.length > 0) {
        e.preventDefault();
        onFiles(files);
      }
    };
    root.addEventListener("paste", handler);
    return () => root.removeEventListener("paste", handler);
  }, [rootRef, onFiles]);

  // Drag-and-drop. dragover must preventDefault to allow the drop, and
  // we toggle a `dragOver` state so the drop zone gets visual feedback.
  useEffect(() => {
    const root = rootRef?.current;
    if (!root) return;
    const onDragOver = (e) => { e.preventDefault(); setDragOver(true); };
    const onDragLeave = (e) => { if (e.target === root) setDragOver(false); };
    const onDrop = (e) => {
      e.preventDefault();
      setDragOver(false);
      const files = e.dataTransfer?.files;
      if (files && files.length > 0) onFiles(files);
    };
    root.addEventListener("dragover", onDragOver);
    root.addEventListener("dragleave", onDragLeave);
    root.addEventListener("drop", onDrop);
    return () => {
      root.removeEventListener("dragover", onDragOver);
      root.removeEventListener("dragleave", onDragLeave);
      root.removeEventListener("drop", onDrop);
    };
  }, [rootRef, onFiles]);

  const remove = (i) => {
    const copy = paths.slice();
    copy.splice(i, 1);
    onChange(copy);
  };

  return (
    <div style={{ marginBottom: T_NEW.s4 }}>
      <div style={{ marginBottom: 8, display: "flex", justifyContent: "space-between",
                    alignItems: "baseline", gap: 8 }}>
        <Mono size={T_NEW.micro} color={T_NEW.ink56}>{t("post.modal.images")}</Mono>
        <span style={{ fontSize: T_NEW.micro, color: T_NEW.ink56, fontFamily: T_NEW.fontSerif,
                       fontStyle: "italic" }}>
          {isTouch ? t("post.modal.hint.touch") : t("post.modal.hint.desktop")}
        </span>
      </div>
      <div
        style={{ display: "flex", flexWrap: "wrap", gap: 8,
                 padding: dragOver ? 10 : 0, borderRadius: T_NEW.r1,
                 transition: "background 160ms",
                 background: dragOver ? T_NEW.persimmonSoft : "transparent",
                 outline: dragOver ? `2px dashed ${T_NEW.persimmon}` : "none" }}
      >
        {paths.map((p, i) => (
          <div key={p.path} style={{ position: "relative", width: 88, height: 88,
                                      borderRadius: T_NEW.r1, overflow: "hidden",
                                      border: `1px solid ${T_NEW.ink12}` }}>
            <img src={p.url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            <button onClick={() => remove(i)}
                    style={{ position: "absolute", top: 4, right: 4, width: 22, height: 22,
                             borderRadius: "50%", background: "rgba(20,17,13,0.78)",
                             border: "none", color: T_NEW.paperOnInk, cursor: "pointer",
                             fontSize: 14, lineHeight: 1, display: "flex",
                             alignItems: "center", justifyContent: "center" }}>×</button>
          </div>
        ))}
        <button
          onClick={() => inputRef.current?.click()}
          style={{ width: 88, height: 88, borderRadius: T_NEW.r1, background: T_NEW.paperSoft,
                   border: `1.5px dashed ${T_NEW.ink12}`, color: T_NEW.ink56, cursor: "pointer",
                   fontSize: T_NEW.micro, display: "flex", flexDirection: "column",
                   alignItems: "center", justifyContent: "center", gap: 6,
                   fontFamily: T_NEW.fontMono, textTransform: "uppercase",
                   letterSpacing: T_NEW.monoTrack, fontWeight: 600 }}>
          {uploading > 0 ? t("post.modal.uploading") : <>{I.plus(T_NEW.ink56)}<span>{t("post.modal.add")}</span></>}
        </button>
        {/* Mobile-only shortcut to the OS camera. `capture` is a hint to
            mobile browsers; desktop browsers ignore it, but we still
            gate rendering on isTouch to keep the desktop layout clean. */}
        {isTouch && (
          <button
            onClick={() => cameraRef.current?.click()}
            style={{ width: 88, height: 88, borderRadius: T_NEW.r1, background: T_NEW.paperSoft,
                     border: `1.5px dashed ${T_NEW.ink12}`, color: T_NEW.ink56, cursor: "pointer",
                     fontSize: T_NEW.micro, display: "flex", flexDirection: "column",
                     alignItems: "center", justifyContent: "center", gap: 4,
                     fontFamily: T_NEW.fontMono, textTransform: "uppercase",
                     letterSpacing: T_NEW.monoTrack, fontWeight: 600 }}>
            <span style={{ fontSize: 22, lineHeight: 1 }}>📷</span>
            <span>{t("post.modal.photo")}</span>
          </button>
        )}
        <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" multiple style={{ display: "none" }}
               onChange={(e) => { if (e.target.files?.length) onFiles(Array.from(e.target.files)); e.target.value = ""; }} />
        <input ref={cameraRef} type="file" accept="image/*" capture="environment" style={{ display: "none" }}
               onChange={(e) => { if (e.target.files?.length) onFiles(Array.from(e.target.files)); e.target.value = ""; }} />
      </div>
      {err && (
        <div style={{ color: T_NEW.danger, fontSize: T_NEW.meta, marginTop: 8,
                      fontFamily: T_NEW.fontSans }}>
          {err}
        </div>
      )}
    </div>
  );
}

export function NewPostModal({ channelSlug, channels, onClose, onDone }) {
  const t = useT();
  const [slug, setSlug] = useState(channelSlug || channels?.[0]?.slug || "");
  const [title, setTitle] = useState("");
  const [bodyRu, setBodyRu] = useState("");
  const [price, setPrice] = useState("");        // dollars as string, e.g. "29.99"
  const [origPrice, setOrigPrice] = useState("");
  const [brand, setBrand] = useState("");
  const [category, setCategory] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");
  const [images, setImages] = useState([]);
  const [publish, setPublish] = useState(false);   // admin: publish immediately
  // Group-buy (СП) controls. mode='group_buy' surfaces the
  // target-qty + deadline pickers; both are optional within group-buy
  // mode (operator can run a fully-manual СТОП with neither).
  const [mode, setMode] = useState("regular");
  const [targetQty, setTargetQty] = useState("");
  const [deadlineLocal, setDeadlineLocal] = useState(""); // datetime-local string
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  // The ref ImageUploader uses to scope paste + drag-drop. We wrap the
  // entire form body in a div with this ref so listeners only fire when
  // the customer is in this modal.
  const rootRef = useRef(null);

  const submit = async () => {
    setErr(""); setLoading(true);
    try {
      const toCents = (s) => {
        if (!s.trim()) return null;
        const n = Math.round(parseFloat(s) * 100);
        return isNaN(n) ? null : n;
      };
      // datetime-local from the browser is in the user's TZ; convert to
      // an ISO string so the server parse works the same way.
      const deadlineIso = (mode === "group_buy" && deadlineLocal)
        ? new Date(deadlineLocal).toISOString()
        : null;
      await api.createPost({
        channelSlug: slug,
        title: title.trim(),
        bodyRu: bodyRu.trim(),
        priceCents: toCents(price),
        originalPriceCents: toCents(origPrice),
        brand: brand.trim() || null,
        category: category.trim() || null,
        sourceUrl: sourceUrl.trim() || null,
        images: images.map(i => i.path),
        status: publish ? "published" : "pending",
        mode,
        targetQty: (mode === "group_buy" && targetQty.trim()) ? parseInt(targetQty, 10) : null,
        deadlineAt: deadlineIso,
      });
      onDone(); onClose();
    } catch (e) { setErr(e.message); }
    setLoading(false);
  };

  return (
    <Modal title={t("post.modal.title")} onClose={onClose} width={560}>
      {/* rootRef scopes paste + drag-drop listeners. tabIndex=-1 lets
          the div receive focus so paste events fire when the user
          clicks anywhere in the form (Cmd-V on the body, not just on
          an input). */}
      <div ref={rootRef} tabIndex={-1} style={{ outline: "none" }}>
      {/* Channel selector */}
      <div style={{ marginBottom: T_NEW.s4 }}>
        <Mono size={T_NEW.micro} color={T_NEW.ink56} style={{ display: "block", marginBottom: 6 }}>
          {t("post.modal.channel")}
        </Mono>
        <select value={slug} onChange={(e) => setSlug(e.target.value)}
                style={{ width: "100%", background: T_NEW.paper, color: T_NEW.ink,
                         border: `1px solid ${T_NEW.ink12}`, padding: "12px 14px",
                         borderRadius: T_NEW.r1, fontSize: 15, fontFamily: T_NEW.fontSans }}>
          {channels?.map(ch => <option key={ch.id} value={ch.slug}>{ch.name} (#{ch.slug})</option>)}
        </select>
      </div>

      <ImageUploader paths={images} onChange={setImages} rootRef={rootRef} />

      <Inp label={t("post.modal.title.field")} value={title} onChange={setTitle} placeholder={t("post.modal.title.placeholder")} autoFocus />
      <div style={{ marginBottom: T_NEW.s4 }}>
        <Mono size={T_NEW.micro} color={T_NEW.ink56} style={{ display: "block", marginBottom: 6 }}>
          {t("post.modal.desc")}
        </Mono>
        <textarea value={bodyRu} onChange={(e) => setBodyRu(e.target.value)}
                  placeholder={t("post.modal.desc.placeholder")}
                  style={{ width: "100%", background: T_NEW.paper, color: T_NEW.ink,
                           border: `1px solid ${T_NEW.ink12}`, padding: "12px 14px",
                           borderRadius: T_NEW.r1, fontSize: 14, minHeight: 96,
                           resize: "vertical", fontFamily: T_NEW.fontSerif, lineHeight: 1.5 }} />
      </div>

      {/* Two-up rows wrap to single column on narrow widths so labels
          + values stay legible. */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        <div style={{ flex: "1 1 140px", minWidth: 0 }}><Inp label={t("post.modal.price")} value={price} onChange={setPrice} placeholder="29.99" /></div>
        <div style={{ flex: "1 1 140px", minWidth: 0 }}><Inp label={t("post.modal.origPrice")} value={origPrice} onChange={setOrigPrice} placeholder="49.99" /></div>
      </div>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        <div style={{ flex: "1 1 140px", minWidth: 0 }}><Inp label={t("post.modal.brand")} value={brand} onChange={setBrand} placeholder={t("post.modal.brand.placeholder")} /></div>
        <div style={{ flex: "1 1 140px", minWidth: 0 }}><Inp label={t("post.modal.category")} value={category} onChange={setCategory} placeholder={t("post.modal.category.placeholder")} /></div>
      </div>
      <Inp label={t("post.modal.sourceUrl")} value={sourceUrl} onChange={setSourceUrl} placeholder="https://walmart.com/..." />

      {/* СП group-buy controls. Collapsed when mode=regular; the checkbox
          flips it open. Either field is optional — operator can run a
          fully-manual СТОП with neither set. */}
      <div style={{ background: T_NEW.paperSoft, borderRadius: T_NEW.r2, padding: 14,
                    marginBottom: T_NEW.s4, border: `1px solid ${T_NEW.ink12}` }}>
        <label style={{ display: "flex", alignItems: "center", gap: 10, color: T_NEW.ink,
                        fontSize: T_NEW.bodySm, cursor: "pointer", fontFamily: T_NEW.fontSans,
                        fontWeight: 500 }}>
          <input type="checkbox" checked={mode === "group_buy"}
                 onChange={(e) => setMode(e.target.checked ? "group_buy" : "regular")}
                 style={{ accentColor: T_NEW.persimmon, width: 16, height: 16 }} />
          <span>
            <span style={{ fontFamily: T_NEW.fontSerif, fontStyle: "italic", fontWeight: 600 }}>{t("post.modal.gb.tag")}</span>
            {t("post.modal.gb.label")}
          </span>
        </label>
        {mode === "group_buy" && (
          <div style={{ display: "flex", gap: 10, marginTop: 12, flexWrap: "wrap" }}>
            <div style={{ flex: "1 1 130px", minWidth: 0 }}>
              <Mono size={T_NEW.micro} color={T_NEW.ink56} style={{ display: "block", marginBottom: 4 }}>
                {t("post.modal.gb.target")}
              </Mono>
              <input type="number" min="1" step="1" value={targetQty}
                     onChange={(e) => setTargetQty(e.target.value)} placeholder={t("post.modal.gb.target.placeholder")}
                     style={{ width: "100%", background: T_NEW.paper, color: T_NEW.ink,
                              border: `1px solid ${T_NEW.ink12}`, padding: "10px 12px",
                              borderRadius: T_NEW.r1, fontSize: 14, fontFamily: T_NEW.fontMono }} />
            </div>
            <div style={{ flex: "1 1 180px", minWidth: 0 }}>
              <Mono size={T_NEW.micro} color={T_NEW.ink56} style={{ display: "block", marginBottom: 4 }}>
                {t("post.modal.gb.deadline")}
              </Mono>
              <input type="datetime-local" value={deadlineLocal}
                     onChange={(e) => setDeadlineLocal(e.target.value)}
                     style={{ width: "100%", background: T_NEW.paper, color: T_NEW.ink,
                              border: `1px solid ${T_NEW.ink12}`, padding: "10px 12px",
                              borderRadius: T_NEW.r1, fontSize: 14, fontFamily: T_NEW.fontMono }} />
            </div>
          </div>
        )}
      </div>

      <label style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: T_NEW.s4,
                      color: T_NEW.ink56, fontSize: T_NEW.bodySm, cursor: "pointer",
                      fontFamily: T_NEW.fontSans }}>
        <input type="checkbox" checked={publish} onChange={(e) => setPublish(e.target.checked)}
               style={{ accentColor: T_NEW.persimmon, width: 16, height: 16 }} />
        <span>{t("post.modal.publish")} <span style={{ fontStyle: "italic", fontFamily: T_NEW.fontSerif }}>{t("post.modal.publishHint")}</span></span>
      </label>

      {err && (
        <div style={{ color: T_NEW.danger, fontSize: T_NEW.meta, marginBottom: 12,
                      padding: "10px 12px", background: `${T_NEW.danger}14`,
                      border: `1px solid ${T_NEW.danger}33`, borderRadius: T_NEW.r1,
                      fontFamily: T_NEW.fontSans }}>
          {err}
        </div>
      )}
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: T_NEW.s2 }}>
        <Btn variant="ghost" onClick={onClose}>{t("post.modal.cancel")}</Btn>
        <Btn disabled={!slug || !title.trim() || !bodyRu.trim() || loading} onClick={submit}>
          {loading ? t("post.modal.creating") : t("post.modal.create")}
        </Btn>
      </div>
      </div>
    </Modal>
  );
}
